import React from "react";
import {
  AlertTriangle,
  ArrowLeftRight,
  BookX,
  Building2,
  EyeOff,
  Network,
  PackageX,
  ReceiptText,
  WifiOff,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";

const PAINS = [
  { icon: BookX, title: "Sales are recorded, not reconciled", desc: "Transactions happen all day, but the owner still cannot explain the final number." },
  { icon: PackageX, title: "Stock problems arrive as surprises", desc: "Fast movers run out while slow stock quietly holds cash on the shelf." },
  { icon: AlertTriangle, title: "Customer balances lose context", desc: "A name and amount are written down, but payments, due dates, and follow-up drift apart." },
  { icon: EyeOff, title: "Staff activity loses ownership", desc: "When everyone shares the same process without clear attribution, mistakes become harder to explain and correct." },
  { icon: WifiOff, title: "Connectivity interrupts the record", desc: "A weak signal should not force the counter to stop or leave work waiting to be captured." },
  { icon: Network, title: "Decisions arrive too late", desc: "By the time the numbers are assembled, the moment to restock, follow up, or correct has passed." },
  { icon: ReceiptText, title: "Expenses blur the real profit", desc: "Revenue looks healthy until operating costs are gathered somewhere else and the margin tells a different story." },
  { icon: ArrowLeftRight, title: "Handovers reset the day", desc: "When one person leaves the counter, notes, exceptions, and unfinished follow-ups often leave with them." },
  { icon: Building2, title: "Branches drift into separate truths", desc: "Each location keeps moving, but inconsistent records make the combined business difficult to trust." },
];

export const Problem = () => {
  return (
    <section
      id="problem"
      data-testid={LANDING.problem}
      className="bg-[#F7FAF9] py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">The quiet cost of guessing</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-[1.1] text-[#0A2424] tracking-tight">
              The problem is not effort. <br className="hidden md:block" />
              <span className="italic text-[#0E6B6B]">It is everything the business forgets.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[#3a4a4a] leading-relaxed">
              When sales, stock, customer balances, expenses, and staff activity live in different places, the owner inherits the uncertainty. Shopbook closes those gaps while the day is still happening.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PAINS.map((p, i) => (
            <div
              key={p.title}
              className="group rounded-3xl bg-white p-7 border border-[#0E6B6B]/8 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:-translate-y-1 hover:shadow-[0_18px_40px_rgb(0,0,0,0.06)] transition-all duration-300"
              data-testid={`problem-card-${i}`}
            >
              <div className="h-11 w-11 rounded-2xl bg-[#0E6B6B]/8 text-[#0E6B6B] flex items-center justify-center mb-5 group-hover:bg-[#0E6B6B] group-hover:text-white transition-colors">
                <p.icon className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0A2424]">{p.title}</h3>
              <p className="mt-2 text-sm text-[#4B5E5E] leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Problem;
